import { useCallback } from 'react';

import { useProfile } from '@mint-lib/profile-helpers';
import { useQuery } from '@mint-lib/query-client';
import { Metadata } from '@mint-sdk/api-mdm';

import { useService } from './useService.js';

export const PLATFORM_V2_FIELD_URN = 'urn:mint:field:platform_v2';
export const PLATFORM_TYPE_FIELD_URN = 'urn:mint:field:platform_type';

export const OMNI_PLATFORM_TYPE_URN =
  'urn:mint:value:platform_type:activation_connected';
export const VIRTUAL_PLATFORM_TYPE_URN =
  'urn:mint:value:platform_type:activation_not_connected';
export const AD_SERVER_PLATFORM_TYPE_URN =
  'urn:mint:value:platform_type:ad_server';
export const ANALYTICS_PLATFORM_TYPE_URN =
  'urn:mint:value:platform_type:analytics';

type PlatformTypesMap = Record<string, Record<string, unknown> | undefined>;

type MdmPlatformRelationsData = {
  [PLATFORM_V2_FIELD_URN]?: {
    [PLATFORM_TYPE_FIELD_URN]?: PlatformTypesMap;
  };
};

const useMdmPlatformTypeCheckers = (
  relationsData?: MdmPlatformRelationsData,
) => {
  const isPlatformOfType = useCallback(
    (platformUrn: string, platformTypeUrn: string) => {
      const platformTypesMap =
        relationsData?.[PLATFORM_V2_FIELD_URN]?.[PLATFORM_TYPE_FIELD_URN];

      return Boolean(platformTypesMap?.[platformTypeUrn]?.[platformUrn]);
    },
    [relationsData],
  );

  const isOmniPlatform = useCallback(
    (platformUrn: string) =>
      isPlatformOfType(platformUrn, OMNI_PLATFORM_TYPE_URN),
    [isPlatformOfType],
  );

  const isVirtualPlatform = useCallback(
    (platformUrn: string) =>
      isPlatformOfType(platformUrn, VIRTUAL_PLATFORM_TYPE_URN),
    [isPlatformOfType],
  );

  const isAnalyticsPlatform = useCallback(
    (platformUrn: string) =>
      isPlatformOfType(platformUrn, ANALYTICS_PLATFORM_TYPE_URN),
    [isPlatformOfType],
  );

  const isAdServerPlatform = useCallback(
    (platformUrn: string) =>
      isPlatformOfType(platformUrn, AD_SERVER_PLATFORM_TYPE_URN),
    [isPlatformOfType],
  );

  return {
    isOmniPlatform,
    isVirtualPlatform,
    isAnalyticsPlatform,
    isAdServerPlatform,
  };
};

type UseMdmPlatformTypeOptions = {
  enabled?: boolean;
};

export const useMdmPlatformType = (options?: UseMdmPlatformTypeOptions) => {
  const { enabled = true } = options ?? {};
  const { getCompanyField } = useProfile();
  const companyId = getCompanyField('id');
  const shouldFetchPlatformTypes = typeof companyId === 'number' && enabled;

  const [queryClient] = useService({
    name: 'queryClient',
    suspense: true,
  });

  const { data: relationsData, isLoading } = useQuery(
    {
      ...Metadata.getRelatedFieldsMapQueryOptions({
        data: {
          companyId,
          relations: [
            {
              relatedFieldDefinitionUrnId: PLATFORM_V2_FIELD_URN,
              primaryEntitiesUrnMap: {
                [PLATFORM_TYPE_FIELD_URN]: [
                  OMNI_PLATFORM_TYPE_URN,
                  VIRTUAL_PLATFORM_TYPE_URN,
                  AD_SERVER_PLATFORM_TYPE_URN,
                  ANALYTICS_PLATFORM_TYPE_URN,
                ],
              },
            },
          ],
        },
      }),
      enabled: shouldFetchPlatformTypes,
      staleTime: 1000 * 60, // 1 minute
    },
    queryClient,
  );

  return {
    ...useMdmPlatformTypeCheckers(relationsData),
    isLoading,
  };
};
